import { apiClient } from './client';
import { API_BASE_URL, API_ENDPOINTS } from './config';
import { User } from '@/types';

export interface RequestCodeRequest {
  email: string;
}

export interface RequestCodeResponse {
  message: string;
}

export interface VerifyCodeRequest {
  email: string;
  code: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: User;
}

export interface RefreshResponse {
  access_token: string;
  expires_in: number;
}

export interface AuthConfig {
  waitlistEnabled: boolean;
}

const REFRESH_TOKEN_KEY = 'refresh_token';

export const authApi = {
  async getConfig(): Promise<AuthConfig> {
    return await apiClient.get<AuthConfig>(API_ENDPOINTS.AUTH.CONFIG);
  },

  async requestCode(data: RequestCodeRequest): Promise<RequestCodeResponse> {
    return apiClient.post<RequestCodeResponse>(
      API_ENDPOINTS.AUTH.REQUEST_CODE,
      data
    );
  },

  async verifyCode(data: VerifyCodeRequest): Promise<TokenResponse> {
    const response = await apiClient.post<TokenResponse>(
      API_ENDPOINTS.AUTH.VERIFY_CODE,
      data
    );
    apiClient.setToken(response.access_token);
    if (typeof window !== 'undefined') {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
    }
    return response;
  },

  async refresh(): Promise<RefreshResponse> {
    const refreshToken =
      typeof window !== 'undefined'
        ? localStorage.getItem(REFRESH_TOKEN_KEY)
        : null;
    if (!refreshToken) {
      throw new Error('No refresh token');
    }
    // Use fetch directly so we don't send the expired access token (avoids 401 loop)
    const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Refresh failed');
    }
    const response: RefreshResponse = await res.json();
    apiClient.setToken(response.access_token);
    return response;
  },

  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  async me(): Promise<User> {
    return await apiClient.get<User>(API_ENDPOINTS.AUTH.ME);
  },

  logout() {
    apiClient.setToken(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  },
};
